import React, { useEffect } from 'react'
import { AiChatBubble, AiChatBubblePositionEnum, useFloatingChatBubbleContext } from '@ztechapps/ai.webui'
import { routesDef } from '../../config/routes'
import { HelpLink } from './HelpLink'
import { useStoreAiChatOpenContext } from './StoreAiChatOpenContext'
import { useMounted } from '../../hooks/useMounted'

/**
 * Thin bridge component mounted inside `AiChatBubble`'s
 * `FloatingChatBubbleProvider` tree. Pushes the bubble's imperative `open()`
 * function up to `StoreAiChatOpenContext` so the header's Search via AI
 * button can open the bubble on an empty-input click — without requiring
 * us to lift `FloatingChatBubbleProvider` out of `AiChatBubble`.
 */
const FloatingOpenBridge = () => {
  const { open } = useFloatingChatBubbleContext()
  const { registerFloatingOpen } = useStoreAiChatOpenContext()

  useEffect(() => {
    registerFloatingOpen(open)
    return () => registerFloatingOpen(undefined)
  }, [open, registerFloatingOpen])

  return null
}

/**
 * Storefront wrapper around the shared AiChatBubble. Points the chat at the
 * storefront-specific `/ai/chat/storefront` endpoint, wires per-surface URL
 * builders for product/blog result renderers, reads an auto-send message from
 * the StoreAiChatOpenContext (populated by the "Search via AI" header
 * button), and renders a Contact support action inside the error alert so
 * rate-limit denials gracefully fall back to the conversation widget.
 *
 * Mounted client-only: `AiChatBubble` transitively pulls in `@uppy/webcam`
 * (via `AttachFileButton` → `useUppyFileUpload`), which references `location`
 * at module load and throws during Node SSR. The admin is a pure SPA so it
 * doesn't hit this; the storefront SSRs pages, so we delay mounting until
 * after hydration on the client.
 */
export const StoreAiChatBubble = () => {
  const { initialMessage } = useStoreAiChatOpenContext()
  const mounted = useMounted()

  if (!mounted) {
    return null
  }

  return (
    <AiChatBubble
      api="/ai/chat/storefront"
      productUrl={(p) => routesDef.ProductItemPage.altPath({ _id: p._id })}
      blogUrl={(b) => routesDef.PostItemPage.altPath(b._id)}
      autoSendOnOpen={initialMessage}
      // Every "Search via AI" click starts a brand-new chat session so the
      // conversation never appends to whatever session was last loaded.
      // See `AiChat.tsx` Layout's two-stage create-session-then-send effect.
      newSessionOnAutoSend
      helpAction={<HelpLink />}
      // Scope-appropriate suggestion cards: the default admin cards
      // advertise "Search for customers", "Help me create a new order", and
      // "Find guides/blogs/help" — all outside the storefront prompt's scope,
      // so they would lead customers into refusal loops. Replace with three
      // storefront-scope prompts that the prompt can actually answer.
      emptyChatSuggestions={[
        { icon: 'search', text: 'Show me round die sets' },
        { icon: 'tag', text: 'Looking for a product by part number' },
        { icon: 'book-open', text: 'Find blog posts about panel installation' },
      ]}
      // Anchor the FAB + modal to the bottom-left, the storefront's
      // established assistant corner.
      position={AiChatBubblePositionEnum.BottomLeft}
    >
      <FloatingOpenBridge />
    </AiChatBubble>
  )
}
